import { Injectable } from '@angular/core';

/**
 * Pre-loads the coinflip spin sprite atlases as `ImageBitmap`s at app boot.
 *
 * Why an ImageBitmap and not the existing `<img>` + CSS `background-image`
 * pipeline: the atlases are 2160 × 12240 px (~26 MP each). When used as a CSS
 * `background-image`, browsers do *lazy, tile-based rasterization* — only the
 * tile of the atlas that is currently visible at the bg-position is uploaded
 * to the GPU. The CSS spin animation walks through all 204 tiles over 3.2s, so
 * each unique tile pays a first-paint rasterize cost the *first* time the user
 * sees that tile in a session. The "spin skips first ~1s" symptom is the user
 * watching that progressive priming play out; the spin only looks smooth once
 * every tile has been rasterized at least once (took ~3 viewings on real BE).
 *
 * `createImageBitmap()` decodes the full image into a GPU-friendly bitmap up
 * front — one decode, one upload. Combined with a canvas-driven animation in
 * cf-middle that calls `drawImage(bitmap, sx, sy, …)` per frame, every tile
 * becomes a cheap GPU blit instead of a fresh rasterize. The first spin a user
 * ever sees is then identical to the third.
 *
 * Construction is triggered by injecting this service in AppComponent so the
 * fetch + decode runs in parallel with the rest of app boot. By the time the
 * user lands in a coinflip the bitmaps are typically already resident; if not,
 * `getBitmap()` simply awaits the in-flight load.
 */
@Injectable({ providedIn: 'root' })
export class CoinflipSpritesService {
  private blueBitmap: Promise<ImageBitmap> | null = null;
  private redBitmap: Promise<ImageBitmap> | null = null;

  constructor() {
    this.preload();
  }

  private preload(): void {
    this.blueBitmap = this.loadBitmap(
      '/assets/images/coinflip/coinflip_blue_win.png'
    );
    this.redBitmap = this.loadBitmap(
      '/assets/images/coinflip/coinflip_red_win.png'
    );
  }

  private async loadBitmap(url: string): Promise<ImageBitmap> {
    const res = await fetch(url);
    const blob = await res.blob();
    return createImageBitmap(blob);
  }

  getBitmap(side: 1 | 2): Promise<ImageBitmap> {
    if (!this.blueBitmap || !this.redBitmap) this.preload();
    return side === 1 ? this.blueBitmap! : this.redBitmap!;
  }
}
